import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DjayAvatar } from '../../mascot';
import { getProgressToNextLevel } from '../../config/constants';
import { getLearnerNavItems } from './navigationHelpers';
import { dataLayer } from '../../data/dataLayer';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useDjaySelection } from '../../mascot';
import { APP_NAME } from '../../config/constants';
interface SidebarProps {
  currentView: string;
  onNavigate: (view: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}
export function Sidebar({
  currentView,
  onNavigate,
  isCollapsed,
  onToggleCollapse
}: SidebarProps) {
  // Pull required state from the global store
  const {
    state,
    currentLearnerDisplayName,
    viewingLearnerId,
    accounts
  } = useAppStore();
  // Derive values from store
  const stats = state.stats;
  const displayName = currentLearnerDisplayName;
  const activeAccountId = accounts?.activeAccountId || '';
  const activeAccount = accounts?.accountsById?.[activeAccountId];
  const activeAccountRole = viewingLearnerId ? 'learner' : activeAccount?.role || 'learner';
  const isLearner = activeAccountRole === 'learner';
  const isTeacherOrParent = activeAccountRole === 'teacher' || activeAccountRole === 'parent';
  // Avatar handling for learners
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (isLearner) {
      const loadAvatar = async () => {
        const prefs = await dataLayer.getLearnerPreferences();
        if (prefs?.avatarUrl) {
          setAvatarUrl(prefs.avatarUrl);
        }
      };
      loadAvatar();
    }
  }, [isLearner, accounts?.activeAccountId]);
  const {
    selectedVariant
  } = useDjaySelection();
  // Calculate XP progress for the profile section
  const progressToNextLevel = stats?.xp != null ? getProgressToNextLevel(stats.xp).percent : 0;
  // Streak from account state
  const streak = activeAccount?.appState?.streak?.currentTotalStreak || 0;
  return <motion.aside className="hidden md:flex flex-col h-full bg-white border-r-2 border-slate-100 sticky top-0 z-30 shadow-[4px_0_24px_rgba(249,115,22,0.03)]" initial={false} animate={{
    width: isCollapsed ? 88 : 280
  }} transition={{
    type: 'spring',
    stiffness: 300,
    damping: 30
  }} data-id="element-1305">
      {/* Logo Section - Playful */}
      <div className="h-24 flex items-center px-7 relative overflow-hidden" data-id="element-1306">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-orange-50/50 to-transparent pointer-events-none" data-id="element-1307"></div>

        {/* @djay-slot: sidebar-logo */}
        <motion.div className="flex-shrink-0 relative z-10" whileHover={{
        rotate: 10,
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} data-id="element-1308">
          <DjayAvatar size="lg" emotion="happy" variant={selectedVariant} data-id="element-1309" />
        </motion.div>

        <AnimatePresence data-id="element-1310">
          {!isCollapsed && <motion.span initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -10
        }} className="font-black text-slate-800 ml-4 text-2xl tracking-tight whitespace-nowrap overflow-hidden relative z-10" data-id="element-1311">
              {APP_NAME}
            </motion.span>}
        </AnimatePresence>
      </div>

      {/* Navigation Items - Chunky & Colorful */}
      <nav className="flex-1 space-y-1" data-id="element-1312">
        {getLearnerNavItems().map(item => {
        const isActive = currentView === item.id;
        const Icon = item.icon;
        return <motion.button key={item.id} onClick={() => onNavigate(item.id)} className={`
                w-full flex items-center gap-3 px-4 py-3 rounded-lg
                transition-colors group relative
                ${isActive ? 'bg-gradient-to-r from-purple-500/20 to-blue-500/20 text-purple-600' : 'text-gray-600 hover:bg-gray-100'}
              `} whileHover={{
          x: isCollapsed ? 0 : 4
        }} whileTap={{
          scale: 0.98
        }} data-id="element-1313">
              <Icon className={`w-6 h-6 flex-shrink-0 relative z-10 transition-transform duration-300 ${isActive ? 'animate-wiggle' : 'group-hover:scale-110'}`} strokeWidth={isActive ? 2.5 : 2} data-id="element-1314" />
              <AnimatePresence data-id="element-1315">
                {!isCollapsed && <motion.span initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -10
            }} className={`ml-4 whitespace-nowrap overflow-hidden relative z-10 text-base ${isActive ? 'font-black' : 'font-bold'}`} data-id="element-1316">
                    {item.label}
                  </motion.span>}
              </AnimatePresence>
            </motion.button>;
      })}
      </nav>

      {/* Learner Profile Section */}
      {isLearner && !isCollapsed && stats && <div className="mt-auto pt-4 border-t border-gray-200" data-id="element-1317">
          <div className="px-4 py-3" data-id="element-1318">
            {/* Avatar and Name */}
            <div className="flex items-center gap-3 mb-3" data-id="element-1319">
              {/* Avatar with custom image support */}
              {avatarUrl ? <img src={avatarUrl} alt={displayName} className="w-12 h-12 rounded-full object-cover flex-shrink-0" data-id="element-1320" /> : <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-blue-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0" data-id="element-1321">
                  {displayName.charAt(0).toUpperCase()}
                </div>}
              <div className="flex-1 min-w-0" data-id="element-1322">
                <p className="font-semibold text-gray-900 truncate" data-id="element-1323">
                  {displayName}
                </p>
                <p className="text-xs text-gray-500" data-id="element-1324">Level {stats.level}</p>
              </div>
            </div>

            {/* XP Progress */}
            <div className="space-y-1" data-id="element-1325">
              <div className="flex items-center justify-between text-xs text-gray-600" data-id="element-1326">
                <span data-id="element-1327">XP</span>
                <span data-id="element-1328">
                  {stats.xp} / {getProgressToNextLevel(stats.xp).nextLevelXP}
                </span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-1329">
                <motion.div className="h-full bg-gradient-to-r from-purple-500 to-blue-500" initial={{
              width: 0
            }} animate={{
              width: `${progressToNextLevel}%`
            }} transition={{
              duration: 0.5,
              ease: 'easeOut'
            }} data-id="element-1330" />
              </div>
            </div>
          </div>
        </div>}

      {/* Teacher/Parent Profile Section */}
      {isTeacherOrParent && !isCollapsed && <div className="mt-auto pt-4 border-t border-gray-200" data-id="element-1331">
          <div className="px-4 py-3" data-id="element-1332">
            <div className="flex items-center gap-3 mb-3" data-id="element-1333">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-blue-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0" data-id="element-1334">
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0" data-id="element-1335">
                <p className="font-semibold text-gray-900 truncate" data-id="element-1336">
                  {displayName}
                </p>
                <p className="text-xs text-gray-500 capitalize" data-id="element-1337">
                  {activeAccountRole}
                </p>
              </div>
            </div>
          </div>
        </div>}

      {/* Collapse Toggle */}
      <button onClick={onToggleCollapse} className="absolute -right-3.5 top-28 w-7 h-7 bg-white border-2 border-slate-200 rounded-full flex items-center justify-center shadow-sm text-slate-400 hover:text-orange-500 hover:border-orange-300 hover:scale-110 transition-all z-40 focus:outline-none" aria-label={isCollapsed ? 'Uitklappen' : 'Inklappen'} data-id="element-1338">
        {isCollapsed ? <ChevronRight className="w-4 h-4 stroke-[3]" data-id="element-1339" /> : <ChevronLeft className="w-4 h-4 stroke-[3]" data-id="element-1340" />}
      </button>
    </motion.aside>;
}